import { useGlobalStore } from '../lead-capture-form/state/globalStore';
import { getContextDetailsForCG, snakeToCamelCase } from '../lead-capture-form/utils/helper';
import { useAuthorizationStore } from './authorizationStore';
import { JWT_TOKEN_REFRESH_INTERVAL } from './constants';

const getResponseBody = async (res: Response) => {
  try {
    let json = await res.json();
    return snakeToCamelCase(json);
  } catch (e) {
    // when response doesn't have a body
    return {
      message: res?.ok ? 'success' : 'error',
    };
  }
};

const resolveRequest = async (res: Response) => {
  try {
    const parsed = await getResponseBody(res);
    if (res?.ok) {
      return Promise.resolve(parsed);
    } else {
      return Promise.reject({
        status: res?.status,
        statusText: res?.statusText,
        ...parsed,
      });
    }
  } catch (e) {
    return Promise.reject({
      status: res?.status,
      statusText: res?.statusText,
    });
  }
};

const fetchHeaders = (apiToken: string) => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: `jwt ${apiToken}`,
});

type Options = {
  headers?: any;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: any;
};
const apiCall = async (url: string, options: Options = {}) => {
  const referrer = document.referrer;
  const baseUrl = useGlobalStore.getState().baseUrl;
  const urlFormatted = new URL(`${baseUrl}${url}`);
  urlFormatted.searchParams.set('referrer', referrer);
  /** In case of CG inside video inside a showcase */
  const contextDetails = getContextDetailsForCG();
  if (contextDetails) {
    urlFormatted.searchParams.set('context_resource_id', contextDetails.resourceId);
    urlFormatted.searchParams.set('context_resource_type', contextDetails.resourceType);
    if (contextDetails.resourcePassword) {
      urlFormatted.searchParams.set('context_resource_password', contextDetails.resourcePassword);
    }
  }
  const customHeaders = options.headers || {};
  let headers = { ...customHeaders };
  const apiToken = useAuthorizationStore.getState().token || window['jwtToken'];
  if (apiToken) {
    headers = { ...headers, ...fetchHeaders(apiToken) };
  }
  const method = options.method || 'GET';
  const body = method !== 'GET' && options.body ? JSON.stringify(options.body) : undefined;

  const res = await fetch(urlFormatted, {
    method,
    headers,
    body,
  });

  return await resolveRequest(res);
};

/**
 * Controller end point to return access token of type jwt.
 *
 * @returns Access token
 */

const fetchJwtToken = async () => {
  const response = await fetch('/_next/jwt', {
    headers: {
      'X-Requested-With': 'XMLHttpRequest',
    },
  });
  if (response.ok) {
    return response.json().then((data: { token: string }) => {
      const { token } = data;
      return token;
    });
  }
  throw response;
};

setInterval(() => {
  (async () => {
    try {
      const token = await fetchJwtToken();
      if (token) useAuthorizationStore.setState({ token });
    } catch (error) {}
  })();
  // Refresh every JWT_TOKEN_REFRESH_INTERVAL seconds
}, JWT_TOKEN_REFRESH_INTERVAL * 1000);

/**
 * Always use the following methods to do fetch call.
 * This will automatically append the jwt tokens.
 */

export const get = (url: string) => {
  return apiCall(url);
};

export const post = (url: string, body: any) => {
  const options: Options = { method: 'POST', body };
  return apiCall(url, options);
};

export const put = (url: string, body: any) => {
  const options: Options = { method: 'PUT', body };
  return apiCall(url, options);
};

export const remove = (url: string, body: any) => {
  const options: Options = { method: 'DELETE', body };
  return apiCall(url, options);
};
